import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Globe,
  Database,
  Sparkles,
  Send,
  ArrowRight,
  Play,
  Clock,
  Users,
  FileText,
  CheckCircle2,
  Loader2,
  TrendingUp,
  Mail,
  Search,
  X,
  Zap,
  Inbox,
} from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { statsApi } from '../../../api/statsApi';
import { scrapingApi } from '../../../api/scrapingApi';
import { aiGenerationApi } from '../../../api/aiGenerationApi';
import { subscribersApi } from '../../../api/subscribersApi';
import { newsletterDraftsApi } from '../../../api/newsletterDraftsApi';
import { scraperSourcesApi } from '../../../api/scraperSourcesApi';
import { moderatorSchedulerApi } from '../../../api/schedulerApi';
import { subscriberScheduleApi } from '../../../api/subscriberScheduleApi';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { useUiLocale } from '../../../contexts/UiLocaleContext';
import type {
  StatsOverview,
  ScrapingHistoryEntry,
  ClientScheduler,
  ScrapingStatus,
  SubscriberReadinessResponse,
} from '../../../types/api';

type PipelineState = 'idle' | 'scraping' | 'scrape_done' | 'analyzing' | 'analyze_done' | 'ai_generating' | 'ai_done';

type ActivityEntry = {
  id: number | string;
  time: string;
  message: string;
  type: 'scrape' | 'ai' | 'delivery';
};

const MOCK_ACTIVITY: ActivityEntry[] = [];

const MOCK_STATS: StatsOverview = {
  scraped_today: 0,
  pending_analysis: 0,
  active_topics: 0,
  subscriber_count: 0,
};

const GLASS_FRAME =
  'border border-white/50 bg-white/45 shadow-[0_8px_32px_rgba(15,23,42,0.08)] backdrop-blur-xl backdrop-saturate-150';

type PipelineCardTone = 'purple' | 'indigo' | 'blue' | 'green';

function normalizeStatsOverview(data: Partial<StatsOverview> | null | undefined): StatsOverview {
  return {
    scraped_today: Number(data?.scraped_today ?? 0),
    pending_analysis: Number(data?.pending_analysis ?? 0),
    active_topics: Number(data?.active_topics ?? 0),
    subscriber_count: Number(data?.subscriber_count ?? 0),
  };
}

/** Backend uses saved_articles during runs; normalize for the dashboard counter. */
function liveArticlesFromScrapeStatus(s: ScrapingStatus): number {
  const a = s.articles_collected;
  const b = s.saved_articles;
  return Number(a ?? b ?? 0);
}

function deliveryApiError(error: unknown, fallback: string): string {
  const ax = error as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = ax.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((x: { msg?: string }) => x?.msg).filter(Boolean).join(', ') || fallback;
  }
  return ax.message || fallback;
}

/** API returns `{ items: [...] }`; older clients may have used a raw array. */
function normalizeScrapingHistoryPayload(data: unknown): ScrapingHistoryEntry[] {
  if (Array.isArray(data)) return data as ScrapingHistoryEntry[];
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: ScrapingHistoryEntry[] }).items;
  }
  return [];
}


export function ModeratorDashboard() {
  const navigate = useNavigate();
  const { moderatorId } = useAuth() as { moderatorId: number | null };
  const { showSuccess, showError } = useToast() as {
    showSuccess: (message: string, duration?: number) => void;
    showError: (message: string, duration?: number) => void;
  };
  const { t, localeTag } = useUiLocale();

  /* ── Server-state ── */
  const [statsOverview, setStatsOverview] = useState<StatsOverview>(MOCK_STATS);
  const [pipelineSubscribers, setPipelineSubscribers] = useState<{ email: string; topics: string[] }[]>([]);
  const [deliverySubscribers, setDeliverySubscribers] = useState<{ id: number; email: string; status: 'ready' | 'sent' }[]>([]);

  /* ── Scraper Pool & Data Pool state ── */
  const [scraperSources, setScraperSources] = useState<{ url: string; status: string }[]>([]);
  const [scheduleTime, setScheduleTime] = useState<string>('07:00');
  const [lastRunAt, setLastRunAt] = useState<string | null>(null);

  /* ── Pipeline state ── */
  const [pipeline, setPipeline] = useState<PipelineState>('idle');
  const [scrapeProgress, setScrapeProgress] = useState(0);
  const [articlesCollected, setArticlesCollected] = useState(0);
  const statusPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoCheckInFlightRef = useRef(false);
  const startAutoCheckRef = useRef<() => void>(() => {});
  const [isAutoCheckActive, setIsAutoCheckActive] = useState(false);
  const [isAutoCheckRunning, setIsAutoCheckRunning] = useState(false);
  const [lastAutoCheckAt, setLastAutoCheckAt] = useState<string | null>(null);
  const [readinessInfo, setReadinessInfo] = useState<SubscriberReadinessResponse | null>(null);
  const [scrapingHistory, setScrapingHistory] = useState<ScrapingHistoryEntry[]>([]);

  /* ── Pending drafts badge ── */
  const [pendingDraftsCount, setPendingDraftsCount] = useState(0);
  const [triggeringDrafts, setTriggeringDrafts] = useState(false);

  /* ── Delivery state ── */
  const [deliveryMode, setDeliveryMode] = useState<'all' | 'individual'>('all');
  const [selectedSubscribers, setSelectedSubscribers] = useState<string[]>([]);
  const [subscriberSearch, setSubscriberSearch] = useState('');
  const [sendingDelivery, setSendingDelivery] = useState(false);
  const [lastDeliveredAt, setLastDeliveredAt] = useState<string | null>(null);

  const safeScrapedToday = Number(statsOverview.scraped_today ?? 0);
  const safeActiveTopics = Number(statsOverview.active_topics ?? 0);
  const safeSubscriberCount = Number(statsOverview.subscriber_count ?? 0);
  const safePendingAnalysis = Number(statsOverview.pending_analysis ?? 0);
  const readinessSubscribers = readinessInfo?.subscribers ?? [];
  const readySubscriberCount = readinessSubscribers.filter(
    (subscriber) => subscriber.has_draft_today || subscriber.email_sent_today
  ).length;
  const pendingReadinessSubscribers = readinessSubscribers.filter(
    (subscriber) => subscriber.needs_generation
  );
  const readyDeliveryCount = deliverySubscribers.filter((s) => s.status === 'sent').length;
  const scrapeProgressValue = pipeline === 'scraping'
    ? scrapeProgress
    : ['scrape_done', 'analyzing', 'analyze_done', 'ai_generating', 'ai_done'].includes(pipeline)
      ? 100
      : safeScrapedToday > 0
        ? 60
        : 0;
  const aiProgressValue = readinessSubscribers.length > 0
    ? Math.round((readySubscriberCount / readinessSubscribers.length) * 100)
    : pipeline === 'ai_done'
      ? 100
      : 0;
  const deliverProgressValue = deliverySubscribers.length > 0
    ? Math.round((readyDeliveryCount / deliverySubscribers.length) * 100)
    : 0;

  const dataPoolProgressValue =
    pipeline === 'analyzing'
      ? 55
      : ['scrape_done', 'analyze_done', 'ai_generating', 'ai_done'].includes(pipeline)
        ? 100
        : safePendingAnalysis > 0
          ? 35
          : 0;

  const activePipelineStep = useMemo(
    () => resolveActivePipelineStep(pipeline),
    [pipeline],
  );

  const pipelineStepProgresses = useMemo(
    (): [number, number, number, number] =>
      [scrapeProgressValue, dataPoolProgressValue, aiProgressValue, deliverProgressValue],
    [scrapeProgressValue, dataPoolProgressValue, aiProgressValue, deliverProgressValue],
  );

  const pipelineStepRunning = useMemo(
    (): [boolean, boolean, boolean, boolean] => [
      pipeline === 'scraping',
      pipeline === 'analyzing',
      pipeline === 'ai_generating' || isAutoCheckRunning,
      sendingDelivery,
    ],
    [pipeline, isAutoCheckRunning, sendingDelivery],
  );

  const aiGenTotalSubscribers = readinessSubscribers.length || pipelineSubscribers.length;
  const aiGenLastCheckLabel = lastAutoCheckAt
    ? new Date(lastAutoCheckAt).toLocaleTimeString(localeTag, { hour: '2-digit', minute: '2-digit' })
    : '—';

  const activityLog = useMemo<ActivityEntry[]>(() => {
    const rows = Array.isArray(scrapingHistory) ? scrapingHistory : [];
    if (rows.length === 0) return MOCK_ACTIVITY;
    return rows.map((h, i) => ({
      id: h.id ?? i,
      time: new Date(h.started_at).toLocaleTimeString(localeTag, { hour: '2-digit', minute: '2-digit' }),
      message: t('dashboard.activityScraperRun', { status: h.status, count: h.articles_collected ?? 0 }),
      type: 'scrape' as const,
    }));
  }, [scrapingHistory, localeTag, t]);

  /* ── Initial data fetch ── */
  useEffect(() => {
    const controller = new AbortController();

    statsApi.overview({ signal: controller.signal })
      .then((res) => setStatsOverview(normalizeStatsOverview(res.data)))
      .catch(() => { /* fallback: keep MOCK_STATS */ });

    scrapingApi.getHistory({ limit: 15 }, { signal: controller.signal })
      .then((res) => {
        const history = normalizeScrapingHistoryPayload(res.data);
        setScrapingHistory(history);
        if (history.length > 0) {
          setLastRunAt(history[0]?.started_at ?? null);
        }
      })
      .catch(() => { /* keep lastRunAt unset */ });

    subscribersApi.list({ limit: 100, signal: controller.signal })
      .then((res: { data: unknown }) => {
        const subs = (res.data as any[]).map((s) => ({
          id: s.id as number,
          email: s.mail ?? s.email ?? '',
          status: 'ready' as const,
        }));
        if (subs.length > 0) {
          setDeliverySubscribers(subs);
          setPipelineSubscribers(subs.map((s) => ({ email: s.email, topics: [] })));
        }
      })
      .catch(() => { /* fallback: keep empty */ });

    /* Load subscribers + their latest draft status together */
    Promise.allSettled([
      subscribersApi.list({ limit: 100, signal: controller.signal }),
      newsletterDraftsApi.list({ limit: 200 }, { signal: controller.signal }),
    ]).then(([subsRes, draftsRes]) => {
      if (subsRes.status !== 'fulfilled') return;

      /* Build a map: user_id → latest draft status */
      const sentUserIds = new Set<number>();
      if (draftsRes.status === 'fulfilled') {
        const drafts = draftsRes.value.data as { user_id: number; status: string }[];
        /* Group by user_id and keep only the most-recent (API returns newest first) */
        const seen = new Set<number>();
        for (const d of drafts) {
          if (!seen.has(d.user_id)) {
            seen.add(d.user_id);
            if (d.status === 'sent') sentUserIds.add(d.user_id);
          }
        }
      }

      const subs = (subsRes.value.data as any[]).map((s) => ({
        id: s.id as number,
        email: s.mail ?? s.email ?? '',
        status: sentUserIds.has(s.id) ? 'sent' as const : 'ready' as const,
      }));
      if (subs.length > 0) {
        setDeliverySubscribers(subs);
        setPipelineSubscribers(subs.map((s) => ({ email: s.email, topics: [] })));
      }
    });

    /* Scraper sources — for Step 1 card preview */
    scraperSourcesApi.list({}, { signal: controller.signal })
      .then((res) => {
        const sources = Array.isArray(res.data) ? (res.data as { url: string; status: string }[]) : [];
        setScraperSources(sources);
      })
      .catch(() => { /* keep empty */ });

    /* Moderator scheduler — for schedule time display */
    if (moderatorId != null) {
      moderatorSchedulerApi.get(moderatorId, { signal: controller.signal })
        .then((res) => {
          const sch = res.data as ClientScheduler;
          setScheduleTime(sch.morning_time ?? '07:00');
        })
        .catch(() => { /* keep default */ });
    }

    /* Pending drafts count */
    newsletterDraftsApi.listPending({ signal: controller.signal })
      .then((res: { data: unknown }) => {
        const drafts = Array.isArray(res.data) ? res.data : [];
        setPendingDraftsCount(drafts.length);
      })
      .catch(() => { /* keep 0 */ });

    /* Resume scraping indicator if a run is already in progress */
    scrapingApi.getStatus()
      .then((res) => {
        const s = res.data as ScrapingStatus;
        if (s.state === 'start' || s.state === 'running') {
          setPipeline('scraping');
          if (statusPollRef.current) clearInterval(statusPollRef.current);
          statusPollRef.current = setInterval(async () => {
            try {
              const pollResponse = await scrapingApi.getStatus();
              const pollStatus = pollResponse.data as ScrapingStatus;
              setScrapeProgress(pollStatus.progress_pct ?? 0);
              setArticlesCollected(liveArticlesFromScrapeStatus(pollStatus));
              if (pollStatus.state === 'end') {
                clearInterval(statusPollRef.current!);
                setScrapeProgress(100);
                setPipeline('scrape_done');
                void statsApi
                  .overview()
                  .then((res) => setStatsOverview(normalizeStatsOverview(res.data)))
                  .catch(() => {});
                setTimeout(() => startAutoCheckRef.current(), 1200);
              }
            } catch { /* keep polling */ }
          }, 1000);
        }
      })
      .catch(() => { /* ignore — stay idle */ });

    return () => {
      controller.abort();
      if (statusPollRef.current) clearInterval(statusPollRef.current);
      if (autoCheckRef.current) clearInterval(autoCheckRef.current);
    };
  }, [moderatorId]);
  /* ── Pipeline — scraper ── */
  const startScrapePoll = () => {
    if (statusPollRef.current) clearInterval(statusPollRef.current);
    statusPollRef.current = setInterval(async () => {
      try {
        const res = await scrapingApi.getStatus();
        const s = res.data as ScrapingStatus;
        setScrapeProgress(s.progress_pct ?? 0);
        setArticlesCollected(liveArticlesFromScrapeStatus(s));
        if (s.state === 'end') {
          clearInterval(statusPollRef.current!);
          setScrapeProgress(100);
          setPipeline('scrape_done');
          void statsApi
            .overview()
            .then((res) => setStatsOverview(normalizeStatsOverview(res.data)))
            .catch(() => {});
          setTimeout(() => startAutoCheck(), 1200);
        }
      } catch { /* keep polling */ }
    }, 1000);
  };


  const filteredSubscribers = deliverySubscribers.filter((s) =>
    s.email.toLowerCase().includes(subscriberSearch.toLowerCase())
  );

  const allSelectableFiltered = filteredSubscribers.every((s) =>
    selectedSubscribers.includes(s.email)
  );

  const toggleSelectAll = () => {
    if (allSelectableFiltered) {
      setSelectedSubscribers((prev) =>
        prev.filter((x) => !filteredSubscribers.map((s) => s.email).includes(x))
      );
    } else {
      setSelectedSubscribers((prev) => [
        ...new Set([...prev, ...filteredSubscribers.map((s) => s.email)]),
      ]);
    }
  };

  const handleRunScraper = () => {
    if (moderatorId == null) return;

    setPipeline('scraping');
    setScrapeProgress(0);
    setArticlesCollected(0);
    setReadinessInfo(null);
    setLastAutoCheckAt(null);

    moderatorSchedulerApi.runNow(moderatorId, 50).catch(() => {});
    startScrapePoll();
  };

  /* ── Pipeline — analysis only (for existing articles) ── */
  const handleRunAnalysisOnly = () => {
    if (moderatorId == null) return;

    setPipeline('analyzing');
    setReadinessInfo(null);
    setLastAutoCheckAt(null);

    moderatorSchedulerApi.runAnalysisOnly(moderatorId).catch(() => {});

    // Poll scraping status — the analysis-only job sets state to 'running' then 'end'
    if (statusPollRef.current) clearInterval(statusPollRef.current);
    statusPollRef.current = setInterval(async () => {
      try {
        const res = await scrapingApi.getStatus();
        const s = res.data as ScrapingStatus;
        if (s.state === 'end') {
          clearInterval(statusPollRef.current!);
          setPipeline('analyze_done');
          setTimeout(() => startAutoCheck(), 800);
        }
      } catch { /* keep polling */ }
    }, 2000);
  };

  /* ── Pipeline — AI generation / readiness auto-check ── */
  const startAiGeneration = useCallback(async () => {
    if (moderatorId == null || autoCheckInFlightRef.current) return;

    autoCheckInFlightRef.current = true;
    setIsAutoCheckRunning(true);
    setPipeline('ai_generating');

    try {
      const readinessResponse = await aiGenerationApi.subscriberReadiness();
      const initialReadiness = readinessResponse.data;
      setReadinessInfo(initialReadiness);
      setLastAutoCheckAt(new Date().toISOString());
      setPipelineSubscribers(
        initialReadiness.subscribers.map((subscriber) => ({
          email: subscriber.email,
          topics: [],
        }))
      );

      if (initialReadiness.needs_generation_count > 0) {
        await aiGenerationApi.autoGenerateCheck();

        const updatedReadinessResponse = await aiGenerationApi.subscriberReadiness();
        const updatedReadiness = updatedReadinessResponse.data;
        setReadinessInfo(updatedReadiness);
        setPipelineSubscribers(
          updatedReadiness.subscribers.map((subscriber) => ({
            email: subscriber.email,
            topics: [],
          }))
        );
      }

      setPipeline('ai_done');
    } catch {
      setPipeline('scrape_done');
    } finally {
      autoCheckInFlightRef.current = false;
      setIsAutoCheckRunning(false);
    }
  }, [moderatorId]);

  const startAutoCheck = useCallback(() => {
    if (moderatorId == null) return;

    if (autoCheckRef.current) clearInterval(autoCheckRef.current);
    setIsAutoCheckActive(true);
    void startAiGeneration();
    autoCheckRef.current = setInterval(() => {
      void startAiGeneration();
    }, 5 * 60 * 1000);
  }, [moderatorId, startAiGeneration]);

  startAutoCheckRef.current = startAutoCheck;

  /* ── Trigger Drafts handler ── */
  const handleTriggerDrafts = async () => {
    setTriggeringDrafts(true);
    try {
      await moderatorSchedulerApi.triggerDrafts();
      setPipeline('ai_generating');
    } catch { /* ignore */ }
    setTriggeringDrafts(false);
  };

  /* ── Delivery send handler ── */
  const handleSendDelivery = async () => {
    setSendingDelivery(true);
    try {
      if (deliveryMode === 'all') {
        const { data } = await newsletterDraftsApi.sendAll();
        const sent = data?.sent ?? 0;
        const failed = data?.failed ?? 0;
        const skipped = data?.skipped ?? 0;
        showSuccess(
          t('dashboard.toastSendAll', {
            sent,
            failed: failed ? t('dashboard.toastFailed', { n: failed }) : '',
            skipped: skipped ? t('dashboard.toastSkipped', { n: skipped }) : '',
          }),
          5000,
        );
      } else {
        const selected = deliverySubscribers.filter((s) => selectedSubscribers.includes(s.email));
        if (selected.length === 0) {
          showError(t('dashboard.toastSelectOne'), 4000);
          return;
        }
        for (const s of selected) {
          await subscriberScheduleApi.sendNow(s.id);
        }
        showSuccess(t('dashboard.toastSentToMany', { count: selected.length }), 4000);
      }
      setLastDeliveredAt(new Date().toISOString());

      /* Refresh delivery statuses so the list reflects 'sent' immediately */
      newsletterDraftsApi.list({ limit: 200 }).then(({ data }) => {
        const drafts = data as { user_id: number; status: string }[];
        const sentUserIds = new Set<number>();
        const seen = new Set<number>();
        for (const d of (Array.isArray(drafts) ? drafts : [])) {
          if (!seen.has(d.user_id)) {
            seen.add(d.user_id);
            if (d.status === 'sent') sentUserIds.add(d.user_id);
          }
        }
        setDeliverySubscribers((prev) =>
          prev.map((s) => ({ ...s, status: sentUserIds.has(s.id) ? 'sent' as const : s.status }))
        );
      }).catch(() => {
        /* On refresh failure, optimistically mark selected/all as sent */
        if (deliveryMode === 'all') {
          setDeliverySubscribers((prev) => prev.map((s) => ({ ...s, status: 'sent' as const })));
        } else {
          setDeliverySubscribers((prev) =>
            prev.map((s) => selectedSubscribers.includes(s.email) ? { ...s, status: 'sent' as const } : s)
          );
        }
      });

      navigate('/scraper-pool');
    } catch (e) {
      showError(deliveryApiError(e, t('dashboard.requestFailed')), 5000);
    } finally {
      setSendingDelivery(false);
    }
  };

  useEffect(() => {
    if (moderatorId != null && safeScrapedToday > 0 && autoCheckRef.current == null) {
      startAutoCheck();
    }
  }, [moderatorId, safeScrapedToday, startAutoCheck]);

  useEffect(() => {
    return () => {
      if (statusPollRef.current) clearInterval(statusPollRef.current);
      if (autoCheckRef.current) clearInterval(autoCheckRef.current);
    };
  }, []);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col gap-2 overflow-hidden p-2">
      <header className={`shrink-0 rounded-2xl ${GLASS_FRAME}`}>
        <div className="px-4 py-2 sm:px-5 sm:py-2.5">
          <div className="flex w-full min-w-0 items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-800 sm:text-3xl hawk-gradient-text">{t('dashboard.title')}</h1>
              <p className="hawk-page-subtitle mt-0.5 text-sm leading-tight">
                {t('dashboard.subtitle')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Pending drafts badge */}
              {pendingDraftsCount > 0 && (
                <button
                  type="button"
                  onClick={() => navigate('/outbox')}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
                >
                  <Inbox className="h-3 w-3" />
                  {t('dashboard.aiGen.pending', { count: pendingDraftsCount })}
                </button>
              )}
              {/* Trigger Drafts button */}
              <button
                type="button"
                onClick={handleTriggerDrafts}
                disabled={triggeringDrafts}
                className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-800 hover:bg-blue-100 transition-colors disabled:opacity-50"
              >
                {triggeringDrafts ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                {t('dashboard.triggerDrafts')}
              </button>
              {pipeline === 'idle' && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-100 px-2 py-1 text-xs font-semibold text-sky-800">
                  <Clock className="h-3 w-3" />
                  <span>{t('dashboard.scraperPool.scheduled')}</span>
                  <span className="rounded-full bg-sky-200/70 px-2 py-0.5 text-[11px] font-bold tracking-wide">
                    {scheduleTime}
                  </span>
                </span>
              )}
              {pipeline === 'scraping' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                  <Loader2 className="w-3 h-3 animate-spin" /> {t('dashboard.badgeScraperRunning')}
                </span>
              )}
              {pipeline === 'analyzing' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  <Loader2 className="w-3 h-3 animate-spin" /> {t('dashboard.badgeAnalyzing')}
                </span>
              )}
              {(pipeline === 'scrape_done' || pipeline === 'analyze_done' || pipeline === 'ai_generating') && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  <Loader2 className={`w-3 h-3 ${isAutoCheckRunning ? 'animate-spin' : ''}`} />
                  {isAutoCheckRunning ? t('dashboard.badgeCheckingReadiness') : t('dashboard.badgeAutoCheckArmed')}
                </span>
              )}
              {pipeline === 'ai_done' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                  <CheckCircle2 className="w-3 h-3" /> {t('dashboard.badgeEmailsReady')}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-hidden">

        {/* ── Stats Row ── */}
        <div className={`shrink-0`}>
        <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-3 md:grid-cols-4">
          {[
            {
              label: t('dashboard.statSubscribers'),
              value: String(safeSubscriberCount),
              icon: Users,
              pill: 'border-blue-200/80 text-blue-900',
              iconWrap: 'bg-blue-100',
              iconColor: 'text-blue-600',
              valueClass: 'text-blue-900',
            },
            {
              label: t('dashboard.statArticlesToday'),
              value: String(safeScrapedToday),
              icon: Mail,
              pill: 'border-cyan-200/80 text-cyan-900',
              iconWrap: 'bg-cyan-100',
              iconColor: 'text-cyan-600',
              valueClass: 'text-cyan-800',
            },
            {
              label: t('dashboard.statActiveTopics'),
              value: String(safeActiveTopics),
              icon: TrendingUp,
              pill: 'border-emerald-200/80 text-emerald-900',
              iconWrap: 'bg-emerald-100',
              iconColor: 'text-emerald-600',
              valueClass: 'text-emerald-900',
            },
            {
              label: t('dashboard.statPendingAnalysis'),
              value: String(safePendingAnalysis),
              icon: Globe,
              pill: 'border-amber-200/80 text-amber-900',
              iconWrap: 'bg-amber-100',
              iconColor: 'text-amber-600',
              valueClass: 'text-amber-900',
            },
          ].map((stat) => (
            <Card
              key={stat.label}
              className={`relative min-w-0 overflow-hidden rounded-2xl !bg-white/45 ${GLASS_FRAME}`}
            >
              <CardHeader className="relative flex flex-row items-center justify-between gap-3 space-y-0 px-4 py-4 sm:px-5 sm:py-5">
                <div className="min-w-0 flex-1">
                  <CardTitle className={`hawk-outside-curve-pill inline-flex max-w-full border bg-white/80 px-2.5 py-0.5 text-[9px] font-semibold uppercase leading-tight tracking-wider sm:text-[10px] ${stat.pill}`}>
                    {stat.label}
                  </CardTitle>
                  <p className={`mt-1 text-3xl font-bold tabular-nums tracking-tight sm:text-4xl ${stat.valueClass}`}>{stat.value}</p>
                </div>
                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl sm:h-14 sm:w-14 ${stat.iconWrap}`}>
                  <stat.icon className={`h-5 w-5 sm:h-6 sm:w-6 ${stat.iconColor}`} strokeWidth={2} />
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
        </div>

        <div className={`grid min-h-0 flex-1 grid-cols-1 items-stretch gap-2 overflow-hidden rounded-2xl p-2 sm:gap-3 sm:p-3 ${GLASS_FRAME}`}>
          <div className="h-full min-h-0 overflow-hidden">
            {/* ── Pipeline — 3 columns: (1+2) | (3+4) | placeholder ── */}
            <div className="grid h-full min-h-0 flex-1 grid-cols-1 items-stretch gap-3 overflow-y-auto pr-1 lg:grid-cols-3">
              {/* Column 1: Step 1 + Step 2 */}
              <motion.div className="flex min-h-0 flex-1 flex-col gap-2.5">
                <motion.div className="flex min-h-0 flex-1 flex-col">
              {/* Step 1 — Scraper Pool */}
              <PipelineCard
                step={1}
                icon={Globe}
                iconBg="bg-cyan-100"
                iconColor="text-cyan-600"
                label={t('dashboard.scraperPool.title')}
                description={t('dashboard.scraperPool.desc', { time: scheduleTime })}
                tone="purple"
                progress={scrapeProgressValue}
              >
                <div className="flex min-h-0 flex-1 flex-col gap-1">
                  {(() => {
                    const activeSources = scraperSources.filter((s) => s.status === 'active');
                    const previewUrls = activeSources.slice(0, 3).map((s) => s.url);
                    const remainingCount = Math.max(0, activeSources.length - 3);
                    const lastRunLabel = lastRunAt
                      ? (() => {
                          const d = new Date(lastRunAt);
                          const isToday = d.toDateString() === new Date().toDateString();
                          const timeStr = d.toLocaleTimeString(localeTag, { hour: '2-digit', minute: '2-digit' });
                          return isToday
                            ? t('dashboard.scraperPool.today', { time: timeStr })
                            : d.toLocaleDateString(localeTag, { day: 'numeric', month: 'short' }) + ` ${timeStr}`;
                        })()
                      : '—';
                    return (
                      <>
                        <PipelineStatStrip
                          tone="purple"
                          items={[
                            { label: t('dashboard.scraperPool.activeUrls'), value: activeSources.length || '—', emphasis: true },
                            { label: t('dashboard.scraperPool.scheduled'), value: t('dashboard.scraperPool.daily', { time: scheduleTime }), mono: true },
                            { label: t('dashboard.scraperPool.lastRun'), value: lastRunLabel },
                          ]}
                        />

                    {/* Scrape status */}
                    <div className="flex min-h-0 flex-1 flex-col justify-start overflow-y-auto scrollbar-hide">
                      {pipeline === 'idle' && (
                        <div className="space-y-0.5">
                          {/* Last completed run summary (real count from scraper_execution_logs) */}
                          {scrapingHistory.length > 0 && (
                            <div className="mb-1.5 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs font-medium text-green-700">
                              <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                              {t('dashboard.scraperPool.completed', { count: scrapingHistory[0]?.articles_collected ?? 0 })}
                            </div>
                          )}
                          {previewUrls.length > 0 ? (
                            <>
                              {previewUrls.map((url) => (
                                <div key={url} className="flex items-center gap-2 text-xs text-gray-600">
                                  <CheckCircle2 className="h-2.5 w-2.5 shrink-0 text-green-500" />
                                  <span className="truncate">{url}</span>
                                </div>
                              ))}
                              {remainingCount > 0 && (
                                <p className="text-xs text-gray-400">{t('dashboard.scraperPool.more', { count: remainingCount })}</p>
                              )}
                            </>
                          ) : (
                            <p className="text-xs text-gray-400 italic">{t('dashboard.scraperPool.noSources')}</p>
                          )}
                        </div>
                      )}
                      {pipeline === 'scraping' && (
                        <div className="space-y-2">
                          <p className="text-xs text-purple-700 flex items-center gap-1.5 font-medium">
                            <Loader2 className="w-3 h-3 animate-spin" />{' '}
                            {t('dashboard.scraperPool.scraping', { count: articlesCollected })}
                          </p>
                          <div className="w-full bg-purple-100 rounded-full h-1.5 overflow-hidden">
                            <motion.div
                              className="h-full bg-purple-500 rounded-full"
                              style={{ width: `${scrapeProgress}%` }}
                              transition={{ duration: 0.3 }}
                            />
                          </div>
                          <div className="space-y-1">
                            {previewUrls.map((url) => (
                              <div key={url} className="flex items-center gap-2 text-xs text-gray-600">
                                <Loader2 className="w-2.5 h-2.5 animate-spin text-purple-400 flex-shrink-0" />
                                {url}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {(pipeline === 'scrape_done' || pipeline === 'analyze_done' || pipeline === 'ai_generating' || pipeline === 'ai_done') && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 font-medium">
                            <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                            {t('dashboard.scraperPool.completed', { count: articlesCollected })}
                          </div>
                          <div className="space-y-1">
                            {previewUrls.map((url) => (
                              <div key={url} className="flex items-center gap-2 text-xs text-gray-600">
                                <CheckCircle2 className="w-2.5 h-2.5 text-green-500 flex-shrink-0" />
                                {url}
                              </div>
                            ))}
                            {remainingCount > 0 && (
                              <p className="text-xs text-gray-400">{t('dashboard.scraperPool.more', { count: remainingCount })}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}

              <div className="flex gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 gap-1.5 text-xs"
                  onClick={() => navigate('/scraper-pool')}
                >
                  <Play className="w-3 h-3" /> {t('dashboard.scraperPool.manage')}
                </Button>
                <Button
                  size="sm"
                  className="flex-1 gap-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={handleRunScraper}
                  disabled={moderatorId == null || (pipeline !== 'idle' && pipeline !== 'ai_done')}
                >
                  {pipeline === 'scraping' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                  {t('dashboard.scraperPool.runNow')}
                </Button>
              </div>
            </div>
          </PipelineCard>
                </motion.div>
                <motion.div className="flex min-h-0 flex-1 flex-col">
          {/* Step 2 — Data Pool */}
          <PipelineCard
            step={2}
            icon={Database}
            iconBg="bg-gradient-to-br from-cyan-100 via-sky-100 to-blue-100 ring-cyan-200/50"
            iconColor="text-sky-600"
            label={t('dashboard.dataPool.title')}
            description={t('dashboard.dataPool.desc')}
            tone="indigo"
            progress={dataPoolProgressValue}
          >
            <div className="flex min-h-0 flex-1 flex-col gap-2.5">
              <PipelineStatStrip
                tone="indigo"
                items={[
                  {
                    label: t('dashboard.dataPool.articlesToday'),
                    value: safeScrapedToday > 0 ? safeScrapedToday.toLocaleString(localeTag) : '—',
                    emphasis: true,
                  },
                  {
                    label: t('dashboard.dataPool.pendingAnalysis'),
                    value:
                      safePendingAnalysis > 0
                        ? t('dashboard.dataPool.articlesCount', { count: safePendingAnalysis })
                        : '—',
                  },
                  {
                    label: t('dashboard.dataPool.activeTopics'),
                    value: safeActiveTopics > 0 ? safeActiveTopics : '—',
                  },
                ]}
              />

              {pipeline === 'analyzing' && (
                <div className="flex items-center gap-2 rounded-xl border border-sky-200/70 bg-sky-50/80 px-3 py-2 text-xs font-medium text-sky-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] backdrop-blur-sm">
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-sky-600" />
                  {t('dashboard.dataPool.runAnalysis')}…
                </div>
              )}

              <div className="mt-auto flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 w-full shrink-0 gap-1.5 rounded-xl border-white/70 bg-white/55 text-xs font-medium text-slate-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_4px_16px_-8px_rgba(14,165,233,0.25)] ring-1 ring-sky-200/45 backdrop-blur-md transition hover:bg-white/80 hover:text-sky-900"
                  onClick={() => navigate('/collected-raw')}
                >
                  <FileText className="h-3.5 w-3.5 text-sky-600" /> {t('dashboard.dataPool.viewArticles')}
                </Button>
                <Button
                  size="sm"
                  className="h-11 w-full shrink-0 gap-2 rounded-xl border-0 bg-gradient-to-r from-cyan-500 via-sky-500 to-blue-600 text-sm font-semibold text-white shadow-[0_10px_28px_-10px_rgba(14,165,233,0.65),inset_0_1px_0_rgba(255,255,255,0.35)] transition hover:from-cyan-400 hover:via-sky-500 hover:to-blue-500 hover:brightness-105 disabled:opacity-55"
                  onClick={handleRunAnalysisOnly}
                  disabled={moderatorId == null || (pipeline !== 'idle' && pipeline !== 'ai_done')}
                  title={t('dashboard.dataPool.runAnalysisTitle')}
                >
                  {pipeline === 'analyzing' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  {t('dashboard.dataPool.runAnalysis')}
                </Button>
              </div>
            </div>
          </PipelineCard>
                </motion.div>
              </motion.div>

              {/* Column 2: Step 3 + Step 4 */}
              <motion.div className="flex min-h-0 flex-1 flex-col gap-2.5">
                <motion.div className="flex min-h-0 flex-1 flex-col">
          {/* Step 3 — AI Generation */}
          <PipelineCard
            step={3}
            icon={Sparkles}
            iconBg="bg-indigo-100"
            iconColor="text-indigo-600"
            label={t('dashboard.aiGen.title')}
            description={t('dashboard.aiGen.desc')}
            tone="blue"
            progress={aiProgressValue}
          >
            <div className="flex min-h-0 flex-1 flex-col gap-1">
              <PipelineStatStrip
                tone="blue"
                items={[
                  {
                    label: t('dashboard.aiGen.subscribers'),
                    value: aiGenTotalSubscribers,
                    emphasis: true,
                  },
                ]}
              />

              <div className="flex min-h-0 flex-1 flex-col gap-2">
                {pipeline === 'idle' && (
                  <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                      <Clock className="h-4 w-4 text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-400">{t('dashboard.aiGen.waitingDrafts')}</p>
                  </div>
                )}

                {pipeline === 'scraping' && (
                  <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-50">
                      <Loader2 className="h-4 w-4 animate-spin text-purple-400" />
                    </div>
                    <p className="text-xs text-gray-400">{t('dashboard.aiGen.waitingScraper')}</p>
                  </div>
                )}

                {pipeline === 'analyzing' && (
                  <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
                    </div>
                    <p className="text-xs text-gray-400">{t('dashboard.aiGen.runningAnalysis')}</p>
                  </div>
                )}

                {(pipeline === 'scrape_done' || pipeline === 'analyze_done' || pipeline === 'ai_generating') && (
                  <div className="flex h-full flex-col gap-2">
                    <div className="flex-shrink-0 space-y-1">
                      <PipelineStatStrip
                        tone="blue"
                        items={[
                          {
                            label: t('dashboard.aiGen.autoCheckEvery'),
                            value: (
                              <span className="inline-flex items-center gap-1">
                                <Loader2 className={`h-3 w-3 shrink-0 text-blue-500 ${isAutoCheckRunning ? 'animate-spin' : ''}`} />
                                {isAutoCheckActive ? t('dashboard.aiGen.autoCheckActive') : t('dashboard.aiGen.autoCheckIdle')}
                              </span>
                            ),
                          },
                          {
                            label: t('dashboard.aiGen.lastCheck', { time: '' }).trim(),
                            value: aiGenLastCheckLabel,
                          },
                        ]}
                      />
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-blue-100">
                        <motion.div
                          className="h-full rounded-full bg-blue-500"
                          animate={{
                            width: `${aiGenTotalSubscribers > 0 ? (readySubscriberCount / aiGenTotalSubscribers) * 100 : 0}%`,
                          }}
                          transition={{ duration: 0.4 }}
                        />
                      </div>
                    </div>

                    <div className="flex flex-shrink-0 items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                      <span>
                        {isAutoCheckActive ? t('dashboard.aiGen.autoCheckActive') : t('dashboard.aiGen.autoCheckIdle')}
                      </span>
                      <span className="font-semibold">
                        {t('dashboard.aiGen.pending', { count: readinessInfo?.needs_generation_count ?? 0 })}
                      </span>
                    </div>

                    <div className="min-h-0 flex-1 space-y-1 overflow-y-auto scrollbar-hide">
                      {readinessSubscribers.length === 0 && (
                        <p className="text-xs italic text-gray-400">{t('dashboard.aiGen.noReadiness')}</p>
                      )}
                      {readinessSubscribers.map((sub) => {
                        const isReady = sub.has_draft_today || sub.email_sent_today;
                        return (
                          <div
                            key={sub.user_id}
                            className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${
                              isReady
                                ? 'border-green-200 bg-green-50 text-green-800'
                                : sub.needs_generation
                                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                                  : 'border-gray-200 bg-gray-50 text-gray-700'
                            }`}
                          >
                            <span className="truncate">{sub.email}</span>
                            <span className="whitespace-nowrap font-medium">
                              {sub.email_sent_today
                                ? t('dashboard.aiGen.sentToday')
                                : sub.has_draft_today
                                  ? t('dashboard.aiGen.draftReady')
                                  : sub.needs_generation
                                    ? t('dashboard.aiGen.pendingGeneration')
                                    : t('dashboard.aiGen.noTopics')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {pipeline === 'ai_done' && (
                  <div className="flex h-full flex-col gap-2">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex flex-shrink-0 items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-700"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                      {pendingReadinessSubscribers.length === 0
                        ? t('dashboard.aiGen.allReady', { count: aiGenTotalSubscribers })
                        : t('dashboard.aiGen.someNeedDrafts', { count: pendingReadinessSubscribers.length })}
                    </motion.div>
                    <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto scrollbar-hide">
                      {readinessSubscribers.map((sub, index) => (
                        <motion.div
                          key={sub.user_id}
                          initial={{ opacity: 0, x: -4 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.03 }}
                          className="flex items-center justify-between px-1 text-xs"
                        >
                          <span className="truncate text-gray-600">{sub.email}</span>
                          <span
                            className={`font-medium ${
                              sub.email_sent_today || sub.has_draft_today ? 'text-green-600' : 'text-amber-600'
                            }`}
                          >
                            {sub.email_sent_today
                              ? t('dashboard.aiGen.sentToday')
                              : sub.has_draft_today
                                ? t('dashboard.aiGen.draftReady')
                                : t('dashboard.aiGen.pendingShort')}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </PipelineCard>
                </motion.div>
                <motion.div className="flex min-h-0 flex-1 flex-col">
          {/* Step 4 — Delivery */}
          <PipelineCard
            step={4}
            icon={Send}
            iconBg="bg-green-100"
            iconColor="text-green-600"
            label={t('dashboard.delivery.title')}
            description={t('dashboard.delivery.desc')}
            tone="green"
            progress={deliverProgressValue}
          >
            <div className="flex min-h-0 flex-1 flex-col gap-1">
              <div className="flex flex-shrink-0 gap-1.5 rounded-lg bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => {
                    setDeliveryMode('all');
                    setSelectedSubscribers([]);
                    setSubscriberSearch('');
                  }}
                  className={`flex-1 rounded px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    deliveryMode === 'all'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t('dashboard.delivery.sendAll')}
                </button>
                <button
                  type="button"
                  onClick={() => setDeliveryMode('individual')}
                  className={`flex-1 rounded px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    deliveryMode === 'individual'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t('dashboard.delivery.individual')}
                </button>
              </div>

              <div className="flex min-h-0 flex-1 flex-col gap-2">
                {deliveryMode === 'all' ? (
                  <>
                    <PipelineStatStrip
                      tone="green"
                      items={[
                        {
                          label: t('dashboard.delivery.activeSubscribers'),
                          value: deliverySubscribers.length,
                          emphasis: true,
                        },
                        {
                          label: t('dashboard.delivery.lastDelivery'),
                          value: lastDeliveredAt
                            ? (() => {
                                const d = new Date(lastDeliveredAt);
                                const isToday = d.toDateString() === new Date().toDateString();
                                const timeStr = d.toLocaleTimeString(localeTag, {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                });
                                return isToday
                                  ? t('dashboard.scraperPool.today', { time: timeStr })
                                  : d.toLocaleDateString(localeTag, { day: '2-digit', month: '2-digit' });
                              })()
                            : '—',
                        },
                        {
                          label: t('dashboard.delivery.pendingSend'),
                          value: (
                            <span className="text-amber-600">
                              {t('dashboard.delivery.readyCount', {
                                count: deliverySubscribers.filter((s) => s.status === 'ready').length,
                              })}
                            </span>
                          ),
                        },
                      ]}
                    />
                    <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto scrollbar-hide pt-1">
                      {deliverySubscribers.length === 0 ? (
                        <p className="py-3 text-center text-xs text-gray-400">
                          {t('dashboard.delivery.noSubscribers')}
                        </p>
                      ) : (
                        <>
                          {deliverySubscribers.slice(0, 3).map((s) => (
                            <div key={s.id} className="flex items-center justify-between text-xs">
                              <span className="truncate text-gray-600">{s.email}</span>
                              <span className={s.status === 'sent' ? 'text-green-600' : 'text-amber-600'}>
                                {s.status === 'sent' ? t('dashboard.delivery.sent') : t('dashboard.delivery.ready')}
                              </span>
                            </div>
                          ))}
                          {deliverySubscribers.length > 3 && (
                            <p className="pt-0.5 text-[10px] text-gray-400">
                              {t('dashboard.delivery.moreSubscribers', {
                                count: deliverySubscribers.length - 3,
                              })}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <div className="relative min-w-0 flex-1">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder={t('dashboard.delivery.searchPlaceholder')}
                          value={subscriberSearch}
                          onChange={(e) => setSubscriberSearch(e.target.value)}
                          className="h-8 w-full rounded-lg border border-slate-200 bg-white/85 py-1.5 pl-7 pr-3 text-xs shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
                        />
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <span className="whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700">
                          {selectedSubscribers.length}/{deliverySubscribers.length}
                        </span>
                        <button
                          type="button"
                          onClick={toggleSelectAll}
                          disabled={filteredSubscribers.length === 0}
                          className={`h-8 rounded-lg border px-3 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                            allSelectableFiltered && filteredSubscribers.length > 0
                              ? 'border-emerald-300 bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700'
                          }`}
                        >
                          All
                        </button>
                      </div>
                    </div>

                    {selectedSubscribers.length > 0 && (
                      <div className="flex max-h-16 flex-shrink-0 flex-wrap gap-1 overflow-y-auto rounded-lg border border-blue-100/70 bg-blue-50/45 p-1.5">
                        {selectedSubscribers.map((email) => (
                          <span
                            key={email}
                            className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"
                          >
                            <span className="max-w-[80px] truncate">{email}</span>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedSubscribers((prev) => prev.filter((x) => x !== email))
                              }
                              className="text-blue-600 transition-colors hover:text-blue-900"
                            >
                              <X className="h-2.5 w-2.5" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto scrollbar-hide">
                      {filteredSubscribers.length === 0 ? (
                        <p className="py-3 text-center text-xs text-gray-400">
                          {t('dashboard.delivery.noSubscribers')}
                        </p>
                      ) : (
                        filteredSubscribers.map((s) => (
                          <label
                            key={s.email}
                            className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs transition-colors hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={selectedSubscribers.includes(s.email)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSubscribers((prev) => [...prev, s.email]);
                                } else {
                                  setSelectedSubscribers((prev) => prev.filter((x) => x !== s.email));
                                }
                              }}
                              className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 text-green-600"
                            />
                            <span className="flex-1 text-gray-600">{s.email}</span>
                            <span className={s.status === 'sent' ? 'text-green-600' : 'text-amber-600'}>
                              {s.status === 'sent' ? '✓' : '●'}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>

              <Button
                size="sm"
                className="w-full flex-shrink-0 gap-1.5 bg-emerald-500 text-xs text-white shadow-sm hover:bg-emerald-600 disabled:opacity-50"
                onClick={handleSendDelivery}
                disabled={
                  (deliveryMode === 'individual' && selectedSubscribers.length === 0) || sendingDelivery
                }
              >
                {sendingDelivery ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                {deliveryMode === 'all'
                  ? t('dashboard.delivery.sendAllNow')
                  : selectedSubscribers.length === 1
                    ? t('dashboard.delivery.sendToOne')
                    : t('dashboard.delivery.sendToMany', { count: selectedSubscribers.length })}
              </Button>
            </div>
          </PipelineCard>
                </motion.div>
              </motion.div>

              {/* Column 3: pipeline stepper */}
              <PipelineStepperPanel
                activeStep={activePipelineStep}
                progresses={pipelineStepProgresses}
                running={pipelineStepRunning}
                scheduleTime={scheduleTime}
              />
            </div>
          </div>

          {/*
          ── Recent Pipeline Activity (unused for now — restore + ActivityEntry/activityLog if needed) ──
          <div className="h-full max-h-full min-h-0 overflow-hidden">
            <Card className="flex h-full min-h-0 max-h-full flex-col overflow-hidden border border-slate-200 bg-white shadow-sm">
              <CardHeader className="border-b border-slate-200 bg-white pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">
                      <span className="text-slate-800">Recent Pipeline Activity</span>
                    </CardTitle>
                    <CardDescription className="text-xs text-slate-600">Latest events across scrape, AI generation, and delivery</CardDescription>
                  </div>
                  <div className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600">
                    <Clock className="w-4 h-4" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="min-h-0 flex-1 overflow-hidden p-0">
                <div className="h-full min-h-0 overflow-y-auto overscroll-contain divide-y divide-slate-200/80">
                  {activityLog.map((entry, i) => {
                    const Icon = activityIcon[entry.type];
                    return ( 
                      <motion.div
                        key={entry.id}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04 }}
                        className="flex items-start gap-3 px-5 py-3"
                      >
                        <div className={`mt-0.5 w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 border ${activityColor[entry.type]}`}>
                          <Icon className="w-3 h-3" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700">{entry.message}</p>
                        </div>
                        <span className="text-xs text-slate-500 font-mono flex-shrink-0">{entry.time}</span>
                      </motion.div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
          */}
        </div>

      </div>
    </div>
  );
}

/* ── Sub-components ── */

interface PipelineStatItem {
  label: string;
  value: React.ReactNode;
  emphasis?: boolean;
  mono?: boolean;
}

function pipelineStatGridCols(count: number): string {
  if (count <= 1) return 'grid-cols-1';
  if (count === 2) return 'grid-cols-2';
  if (count === 3) return 'grid-cols-3';
  return 'grid-cols-4';
}

function PipelineStatStrip({
  items,
  tone = 'blue',
}: {
  items: PipelineStatItem[];
  tone?: PipelineCardTone;
}) {
  if (items.length === 0) return null;
  const { strip } = pipelineCardToneStyles(tone);

  return (
    <div
      className={`grid shrink-0 ${pipelineStatGridCols(items.length)} py-1.5 sm:py-2 ${strip.shell}`}
    >
      {items.map((item) => (
        <div key={item.label} className="min-w-0 px-2 sm:px-2.5">
          <p className={`truncate text-[10px] sm:text-[11px] ${strip.label}`}>{item.label}</p>
          <div
            className={
              item.emphasis
                ? `truncate text-sm font-bold leading-tight sm:text-base ${strip.valueEmphasis}`
                : item.mono
                  ? `truncate font-mono text-[11px] font-semibold leading-tight sm:text-xs ${strip.valueMono}`
                  : `truncate text-[11px] font-medium leading-tight sm:text-xs ${strip.value}`
            }
          >
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );
}

interface PipelineCardProps {
  step: number;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  label: string;
  description: string;
  children: React.ReactNode;
  tone?: 'purple' | 'indigo' | 'blue' | 'green';
  progress?: number;
  arrowRight?: boolean;
  arrowDown?: boolean;
}

function pipelineCardToneStyles(tone: PipelineCardTone) {
  const glassBase =
    'rounded-2xl border border-white/50 shadow-[0_8px_32px_rgba(15,23,42,0.08)] backdrop-blur-xl backdrop-saturate-150';

  switch (tone) {
    case 'purple':
      return {
        card: `${glassBase} !bg-cyan-50/45 ring-1 ring-cyan-200/40`,
        progress: 'bg-cyan-500',
        bar: 'rounded-xl border border-white/55 bg-white/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_2px_14px_-4px_rgba(6,182,212,0.2)] ring-1 ring-cyan-200/45 backdrop-blur-xl backdrop-saturate-150',
        badge: 'bg-cyan-500/15 text-cyan-900 ring-1 ring-cyan-300/40 backdrop-blur-sm',
        strip: {
          shell:
            'divide-x divide-cyan-200/40 rounded-xl border border-white/50 bg-white/35 shadow-[0_4px_24px_-8px_rgba(6,182,212,0.22),inset_0_1px_0_rgba(255,255,255,0.92)] ring-1 ring-cyan-300/35 backdrop-blur-xl backdrop-saturate-150',
          label: 'text-cyan-800/75',
          value: 'text-slate-700',
          valueEmphasis: 'text-cyan-950',
          valueMono: 'text-cyan-900',
        },
      };
    case 'indigo':
      return {
        card: `${glassBase} !bg-sky-50/50 ring-1 ring-sky-200/45`,
        progress: 'bg-gradient-to-r from-cyan-500 via-sky-500 to-blue-600',
        bar: 'rounded-xl border border-white/55 bg-white/45 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_2px_14px_-4px_rgba(14,165,233,0.22)] ring-1 ring-sky-200/50 backdrop-blur-xl backdrop-saturate-150',
        badge: 'bg-gradient-to-r from-cyan-500/15 to-blue-500/15 text-sky-900 ring-1 ring-sky-300/45 backdrop-blur-sm',
        strip: {
          shell:
            'divide-x divide-sky-200/45 rounded-xl border border-white/55 bg-gradient-to-br from-white/50 via-cyan-50/30 to-sky-50/40 shadow-[0_4px_24px_-8px_rgba(14,165,233,0.24),inset_0_1px_0_rgba(255,255,255,0.95)] ring-1 ring-sky-300/40 backdrop-blur-xl backdrop-saturate-150',
          label: 'text-sky-800/75',
          value: 'text-slate-700',
          valueEmphasis: 'text-sky-950',
          valueMono: 'text-sky-900',
        },
      };
    case 'green':
      return {
        card: `${glassBase} !bg-emerald-50/45 ring-1 ring-emerald-200/40`,
        progress: 'bg-emerald-500',
        bar: 'rounded-xl border border-white/55 bg-white/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_2px_14px_-4px_rgba(16,185,129,0.2)] ring-1 ring-emerald-200/45 backdrop-blur-xl backdrop-saturate-150',
        badge: 'bg-emerald-500/15 text-emerald-900 ring-1 ring-emerald-300/40 backdrop-blur-sm',
        strip: {
          shell:
            'divide-x divide-emerald-200/40 rounded-xl border border-white/50 bg-white/35 shadow-[0_4px_24px_-8px_rgba(16,185,129,0.2),inset_0_1px_0_rgba(255,255,255,0.92)] ring-1 ring-emerald-300/35 backdrop-blur-xl backdrop-saturate-150',
          label: 'text-emerald-800/75',
          value: 'text-slate-700',
          valueEmphasis: 'text-emerald-950',
          valueMono: 'text-emerald-900',
        },
      };
    default:
      return {
        card: `${glassBase} !bg-indigo-50/45 ring-1 ring-indigo-200/40`,
        progress: 'bg-indigo-500',
        bar: 'rounded-xl border border-white/55 bg-white/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_2px_14px_-4px_rgba(79,70,229,0.2)] ring-1 ring-indigo-200/45 backdrop-blur-xl backdrop-saturate-150',
        badge: 'bg-indigo-500/15 text-indigo-900 ring-1 ring-indigo-300/40 backdrop-blur-sm',
        strip: {
          shell:
            'divide-x divide-indigo-200/40 rounded-xl border border-white/50 bg-white/35 shadow-[0_4px_24px_-8px_rgba(79,70,229,0.2),inset_0_1px_0_rgba(255,255,255,0.92)] ring-1 ring-indigo-300/35 backdrop-blur-xl backdrop-saturate-150',
          label: 'text-indigo-800/75',
          value: 'text-slate-700',
          valueEmphasis: 'text-indigo-950',
          valueMono: 'text-indigo-900',
        },
      };
  }
}

function PipelineCard({
  step,
  icon: Icon,
  iconBg,
  iconColor,
  label,
  description,
  children,
  tone = 'blue',
  progress = 0,
  arrowRight,
  arrowDown,
}: PipelineCardProps) {
  const { t } = useUiLocale();
  const toneStyles = pipelineCardToneStyles(tone);

  return (
    <div className="relative flex h-full min-h-0 min-w-0 flex-col">
      <Card className={`relative flex h-full min-h-0 min-w-0 flex-col gap-1.5 overflow-hidden hover:brightness-100 ${toneStyles.card}`}>
        <CardHeader className="relative z-[1] min-w-0 space-y-0 px-3 pb-1.5 pt-2.5 sm:px-4 sm:pb-2 sm:pt-3">
          <div className="flex min-w-0 max-w-full items-center gap-2.5 sm:gap-3">
            <div
              className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/70 ring-1 ring-white/80 shadow-sm sm:h-11 sm:w-11 ${iconBg}`}
            >
              <Icon className={`h-5 w-5 sm:h-[22px] sm:w-[22px] ${iconColor}`} strokeWidth={2} />
            </div>
            <div
              className={`flex w-0 min-w-0 flex-1 items-center gap-2 rounded-xl border py-1.5 pl-2 pr-3 sm:gap-2.5 sm:py-2 sm:pl-2.5 sm:pr-3.5 ${toneStyles.bar}`}
            >
              <span
                className={`inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] ring-1 sm:text-[10px] ${toneStyles.badge}`}
              >
                {t('dashboard.step', { n: step })}
              </span>
              <span className="h-3.5 w-px shrink-0 bg-slate-200/90" aria-hidden />
              <CardTitle className="shrink-0 text-sm font-semibold tracking-tight text-slate-900 sm:text-[15px]">
                {label}
              </CardTitle>
              <span className="hidden h-1 w-1 shrink-0 rounded-full bg-slate-300 sm:block" aria-hidden />
              <div className="min-w-0 flex-1 overflow-hidden">
                <CardDescription className="truncate text-[11px] leading-snug text-slate-500/90 sm:text-xs">
                  {description}
                </CardDescription>
              </div>
            </div>
          </div>
          <div className="mt-2 space-y-0.5">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-slate-500">{t('dashboard.progress')}</span>
              <span className="font-semibold text-slate-700">{Math.max(0, Math.min(100, Math.round(progress)))}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/50 ring-1 ring-white/40">
              <motion.div
                className={`h-1.5 rounded-full ${toneStyles.progress}`}
                initial={{ width: 0 }}
                animate={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="relative z-[1] flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-2.5 pt-0 sm:px-4 sm:pb-3">{children}</CardContent>
      </Card>

      {/* Right arrow — between step 1→2 and 3→4 */}
      {arrowRight && (
        <div className="hidden sm:flex absolute -right-[18px] top-1/2 -translate-y-1/2 z-10 h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-slate-50">
          <ArrowRight className="w-4 h-4 text-slate-500" />
        </div>
      )}

      {/* Down arrow — below step 2, pointing into step 4 */}
      {arrowDown && (
        <div className="hidden sm:flex absolute bottom-[-18px] left-1/2 -translate-x-1/2 z-10 h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-slate-50">
          <ArrowRight className="w-4 h-4 text-slate-500 rotate-90" />
        </div>
      )}
    </div>
  );
}

type PipelineStepStatus = 'upcoming' | 'active' | 'complete';

function resolveActivePipelineStep(pipeline: PipelineState): number {
  switch (pipeline) {
    case 'scraping':
      return 1;
    case 'scrape_done':
    case 'analyzing':
      return 2;
    case 'analyze_done':
    case 'ai_generating':
      return 3;
    case 'ai_done':
      return 4;
    default:
      return 1;
  }
}

const PIPELINE_STEPPER_STEPS = [
  { step: 1, icon: Globe, tone: 'cyan' as const },
  { step: 2, icon: Database, tone: 'indigo' as const },
  { step: 3, icon: Sparkles, tone: 'blue' as const },
  { step: 4, icon: Send, tone: 'emerald' as const },
];

const STEPPER_TONE = {
  cyan: {
    ring: 'ring-cyan-400/50',
    bg: 'bg-cyan-500',
    soft: 'bg-cyan-100 text-cyan-700',
    bar: 'bg-cyan-500',
    line: 'from-cyan-400 to-cyan-500',
  },
  indigo: {
    ring: 'ring-sky-400/50',
    bg: 'bg-gradient-to-br from-cyan-500 to-blue-600',
    soft: 'bg-sky-100 text-sky-700',
    bar: 'bg-gradient-to-r from-cyan-500 via-sky-500 to-blue-600',
    line: 'from-cyan-400 to-blue-500',
  },
  blue: {
    ring: 'ring-indigo-400/50',
    bg: 'bg-indigo-600',
    soft: 'bg-indigo-100 text-indigo-700',
    bar: 'bg-indigo-500',
    line: 'from-indigo-400 to-indigo-600',
  },
  emerald: {
    ring: 'ring-emerald-400/50',
    bg: 'bg-emerald-500',
    soft: 'bg-emerald-100 text-emerald-700',
    bar: 'bg-emerald-500',
    line: 'from-emerald-400 to-emerald-500',
  },
};

interface PipelineStepperPanelProps {
  activeStep: number;
  progresses: [number, number, number, number];
  running: [boolean, boolean, boolean, boolean];
  scheduleTime: string;
}

function PipelineStepperPanel({
  activeStep,
  progresses,
  running,
  scheduleTime,
}: PipelineStepperPanelProps) {
  const { t } = useUiLocale();

  const labels = useMemo(
    () => [
      { title: t('dashboard.scraperPool.title'), desc: t('dashboard.scraperPool.desc', { time: scheduleTime }) },
      { title: t('dashboard.dataPool.title'), desc: t('dashboard.dataPool.desc') },
      { title: t('dashboard.aiGen.title'), desc: t('dashboard.aiGen.desc') },
      { title: t('dashboard.delivery.title'), desc: t('dashboard.delivery.desc') },
    ],
    [t, scheduleTime],
  );

  return (
    <Card className="flex h-full min-h-0 flex-col border border-slate-200 bg-gradient-to-b from-slate-50/90 to-white shadow-sm">
      <CardHeader className="border-b border-slate-100 px-4 pb-3 pt-4">
        <CardTitle className="text-sm font-semibold text-slate-800">{t('dashboard.pipeline')}</CardTitle>
        <CardDescription className="text-xs text-slate-500">
          {t('dashboard.stepOf', { n: activeStep, total: 4 })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col justify-start overflow-y-auto scrollbar-hide px-4 py-4">
        <motion.div className="flex w-full flex-col items-stretch justify-start gap-0">
          {PIPELINE_STEPPER_STEPS.map((def, index) => {
            const stepNum = def.step;
            const status: PipelineStepStatus =
              stepNum < activeStep ? 'complete' : stepNum === activeStep ? 'active' : 'upcoming';
            const progress = Math.max(0, Math.min(100, Math.round(progresses[index] ?? 0)));
            const isRunning = running[index] ?? false;
            const tone = STEPPER_TONE[def.tone];
            const Icon = def.icon;
            const isLast = index === PIPELINE_STEPPER_STEPS.length - 1;

            const connectorFill =
              stepNum < activeStep
                ? 100
                : stepNum === activeStep
                  ? progress
                  : 0;

            return (
              <motion.div
                key={def.step}
                className="flex w-full gap-3"
                initial={false}
                animate={
                  status === 'active'
                    ? { opacity: 1, x: 0 }
                    : status === 'complete'
                      ? { opacity: 1, x: 0 }
                      : { opacity: 0.55, x: 0 }
                }
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex w-10 shrink-0 flex-col items-center">
                  <motion.div
                      className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                        status === 'complete'
                          ? `${tone.bg} border-transparent text-white`
                          : status === 'active'
                            ? `border-white bg-white text-slate-700 shadow-md ring-4 ${tone.ring}`
                            : 'border-slate-200 bg-white text-slate-400'
                      }`}
                      animate={
                        status === 'active'
                          ? { scale: [1, 1.06, 1], boxShadow: ['0 4px 12px rgba(0,0,0,0.08)', '0 8px 20px rgba(37,99,235,0.15)', '0 4px 12px rgba(0,0,0,0.08)'] }
                          : { scale: 1 }
                      }
                      transition={
                        status === 'active'
                          ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }
                          : { duration: 0.3 }
                      }
                    >
                      {status === 'complete' ? (
                        <motion.div
                          initial={{ scale: 0, rotate: -45 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: 'spring', stiffness: 420, damping: 22 }}
                        >
                          <CheckCircle2 className="h-5 w-5" strokeWidth={2.25} />
                        </motion.div>
                      ) : isRunning ? (
                        <Loader2 className={`h-4 w-4 animate-spin ${status === 'active' ? 'text-indigo-600' : ''}`} />
                      ) : (
                        <Icon className="h-4 w-4" strokeWidth={2} />
                      )}
                      {status === 'active' && (
                        <motion.span
                          className={`absolute -inset-1 rounded-full ${tone.bg} opacity-20`}
                          animate={{ scale: [1, 1.35, 1], opacity: [0.35, 0.08, 0.35] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                          aria-hidden
                        />
                      )}
                  </motion.div>

                  {!isLast && (
                    <motion.div
                      className="relative my-1 w-0.5 min-h-[2.75rem] flex-1 overflow-hidden rounded-full bg-slate-200/90 sm:min-h-[3rem]"
                      aria-hidden
                    >
                      <motion.div
                        className={`absolute inset-x-0 top-0 w-full rounded-full bg-gradient-to-b ${tone.line}`}
                        initial={false}
                        animate={{ height: `${connectorFill}%` }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                      />
                      {status === 'active' && isRunning && (
                        <motion.div
                          className={`absolute left-0 right-0 h-3 rounded-full bg-gradient-to-b ${tone.line} opacity-80`}
                          animate={{ top: ['0%', '85%', '0%'] }}
                          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                        />
                      )}
                    </motion.div>
                  )}
                </div>

                <div className={`min-w-0 flex-1 pt-0.5 ${isLast ? 'pb-0' : 'pb-4 sm:pb-5'}`}>
                  <motion.div className="flex items-center justify-between gap-2">
                      <span
                        className={`text-[10px] font-bold uppercase tracking-widest ${
                          status === 'active' ? 'text-slate-700' : 'text-slate-400'
                        }`}
                      >
                        {t('dashboard.step', { n: stepNum })}
                      </span>
                      <motion.span
                        key={`${stepNum}-${progress}`}
                        className={`text-[11px] font-semibold tabular-nums ${
                          status === 'active' ? 'text-slate-800' : 'text-slate-500'
                        }`}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        {progress}%
                      </motion.span>
                    </motion.div>
                    <p
                      className={`mt-0.5 text-sm font-semibold leading-tight ${
                        status === 'active' ? 'text-slate-900' : status === 'complete' ? 'text-slate-700' : 'text-slate-500'
                      }`}
                    >
                      {labels[index]?.title}
                    </p>
                    <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-slate-500">
                      {labels[index]?.desc}
                    </p>
                    <motion.div
                      className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200/80"
                      layout
                    >
                      <motion.div
                        className={`h-full rounded-full bg-gradient-to-r ${tone.line}`}
                        initial={false}
                        animate={{ width: `${status === 'upcoming' ? 0 : progress}%` }}
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </motion.div>
                  </div>
              </motion.div>
            );
          })}
        </motion.div>
      </CardContent>
    </Card>
  );
}
